import { GroupsTabView, type GroupData } from '@valiant/ui-shared'
import {
  useGetGroupsQuery,
  useRemoveUserFromGroupMutation,
  useAddUserToGroupMutation,
} from '@/generated/graphql'
import { useState, useMemo } from 'react'
import { CreateGroupModal } from '@/components/modals/CreateGroupModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

interface GroupsTabProps {
  userId?: string
  searchTerm: string
}

export function GroupsTab({ userId, searchTerm }: GroupsTabProps) {
  const navigate = useNavigate()
  const [{ data: groupsData, fetching: groupsLoading }] = useGetGroupsQuery({
    variables: userId ? { filter: { userId } } : undefined,
    requestPolicy: 'network-only',
  })
  // Get all groups for assignment dropdown
  const [{ data: allGroupsData }] = useGetGroupsQuery({
    variables: undefined,
    pause: !userId, // Only fetch when we have a userId
    requestPolicy: 'network-only',
  })
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [{ fetching: removing }, removeUserFromGroup] = useRemoveUserFromGroupMutation()
  const [{ fetching: assigning }, addUserToGroup] = useAddUserToGroupMutation()

  // Transform GraphQL data to GroupData format
  const groups: GroupData[] = useMemo(() => {
    return (groupsData?.getGroups || []).map((group) => ({
      groupId: group.groupId,
      name: group.name,
      status: group.status,
      imageUrl: group.imageUrl || undefined,
      admin: group.admin,
      companies: group.companies,
      usdValueOfAvailableBalance: group.usdValueOfAvailableBalance,
      groupBps: group.bps,
      liquidityProviderBps: group.liquidityProviderBps,
    }))
  }, [groupsData?.getGroups])

  // Groups available for assignment (not currently assigned to user)
  const availableGroups: GroupData[] = useMemo(() => {
    if (!userId || !allGroupsData?.getGroups || !groupsData?.getGroups) return []

    const userGroupIds = new Set(groupsData.getGroups.map((group) => group.groupId))
    return allGroupsData.getGroups
      .filter((group) => !userGroupIds.has(group.groupId))
      .map((group) => ({
        groupId: group.groupId,
        name: group.name,
        status: group.status,
        imageUrl: group.imageUrl || undefined,
        admin: group.admin,
        companies: group.companies,
        usdValueOfAvailableBalance: group.usdValueOfAvailableBalance,
        groupBps: group.bps,
        liquidityProviderBps: group.liquidityProviderBps,
      }))
  }, [userId, allGroupsData?.getGroups, groupsData?.getGroups])

  const handleUnassignFromGroup = async (groupId: string, groupName: string) => {
    if (!userId) return

    try {
      const result = await removeUserFromGroup({
        input: {
          userId,
          groupId,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`Successfully unassigned from ${groupName}`)
    } catch (error) {
      toast.error('Failed to unassign from group')
      console.error('Error unassigning from group:', error)
    }
  }

  const handleAssignToGroup = async (groupId: string, groupName: string) => {
    if (!userId) return

    try {
      const result = await addUserToGroup({
        input: {
          userId,
          groupId,
          asAdmin: true, // Assuming we're assigning as admin since this is for group admins
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`Successfully assigned to ${groupName}`)
    } catch (error) {
      toast.error('Failed to assign to group')
      console.error('Error assigning to group:', error)
    }
  }

  const handleViewGroup = (groupId: string) => {
    navigate(`/groups/${groupId}`)
  }

  const handleCopyId = (groupId: string) => {
    navigator.clipboard.writeText(groupId)
    toast.success('Copied to clipboard!')
  }

  return (
    <>
      <GroupsTabView
        groups={groups}
        loading={groupsLoading}
        searchTerm={searchTerm}
        title="Groups"
        showCreateButton={!userId}
        showAssignButton={!!userId}
        availableGroups={availableGroups}
        userId={userId}
        onViewGroup={handleViewGroup}
        onCreateGroup={() => setCreateModalOpen(true)}
        onAssignToGroup={handleAssignToGroup}
        onUnassignFromGroup={handleUnassignFromGroup}
        onCopyId={handleCopyId}
        assigning={assigning}
        removing={removing}
      />

      {!userId && <CreateGroupModal open={createModalOpen} onOpenChange={setCreateModalOpen} />}
    </>
  )
}
